"use client";

// styled-components needs a client component (same as trustedBy/faq).
// This does NOT keep the prices out of the HTML: Next still server-renders
// client components into the initial payload, which is the whole point here.
import { IPricingPlan } from "@/helpers/pricingPlans";
import {
	StyledFootnote,
	StyledInner,
	StyledPlan,
	StyledPlanList,
	StyledPlanName,
	StyledPlanNote,
	StyledPlanPrice,
	StyledPricingSummary,
	StyledSubtitle,
	StyledTitle,
} from "./pricingSummary.styles";

export interface IPricingSummaryProps {
	plans: IPricingPlan[];
}

const formatPrice = (
	amount: number,
	currency: string,
	{ showCents = true }: { showCents?: boolean } = {}
) =>
	new Intl.NumberFormat("en-US", {
		style: "currency",
		currency,
		// Cents stay on across a row so $8.00 lines up with $3.50 rather than
		// rendering as a ragged "$8". The free plan reads better as a flat "$0".
		minimumFractionDigits: showCents ? 2 : 0,
		maximumFractionDigits: 2,
	}).format(amount);

/**
 * Server-rendered plan and price summary for /pricing  (DEV-14679).
 *
 * The interactive pricing table above this is an iframe, so its prices never
 * reach this page's HTML. This section puts them there as real text, readable
 * by Google and by crawlers that do not execute JavaScript.
 *
 * Renders nothing when the plans API is unreachable rather than showing an
 * empty shell.
 */
export const PricingSummary = ({ plans }: IPricingSummaryProps) => {
	if (!plans.length) {
		return null;
	}

	return (
		<StyledPricingSummary>
			<StyledInner>
				<StyledTitle>Plans Start Here</StyledTitle>
				<StyledSubtitle>
					List prices for a single widget, billed annually. Building more? Pick
					your widget count above and the price scales with you.
				</StyledSubtitle>

				<StyledPlanList>
					{plans.map((plan) => {
						// This tier is sold annually only, so the yearly figure is the
						// headline. Leading with a monthly price here would advertise a
						// purchase the checkout does not offer.
						const annualOnly =
							!plan.isFree && !plan.monthlyAvailable && !!plan.annualPrice;

						return (
							<StyledPlan key={plan.name}>
								<StyledPlanName>{plan.name}</StyledPlanName>
								<StyledPlanPrice>
									{plan.isFree
										? formatPrice(0, plan.currency, { showCents: false })
										: annualOnly
											? formatPrice(plan.annualPrice as number, plan.currency)
											: formatPrice(plan.monthlyPrice, plan.currency)}
									{!plan.isFree && (
										<span> {annualOnly ? "/year" : "/month"}</span>
									)}
								</StyledPlanPrice>
								<StyledPlanNote>
									{plan.isFree
										? "Free forever. No credit card required."
										: annualOnly
											? `Billed annually — works out at ${formatPrice(
													(plan.annualPrice as number) / 12,
													plan.currency
												)} a month`
											: plan.annualPrice
												? `or ${formatPrice(plan.annualPrice, plan.currency)} billed annually`
												: "Billed monthly"}
								</StyledPlanNote>
							</StyledPlan>
						);
					})}
				</StyledPlanList>

				<StyledFootnote>
					Prices shown are list prices in USD and exclude VAT, which is
					calculated separately based on your country. Discounts may apply at
					checkout.
				</StyledFootnote>
			</StyledInner>
		</StyledPricingSummary>
	);
};
